import React from "react";
import { Instagram, Linkedin } from "lucide-react";

const Footer = ({ className = "" }) => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "About", href: "https://astranova2k26.com/about" },
    { name: "Members", href: "https://astranova2k26.com/members" },
    { name: "Events", href: "https://astranova2k26.com/events" },
    { name: "Schedule", href: "https://astranova2k26.com/clock" },
    { name: "Sponsors", href: "https://astranova2k26.com/sponsors" },
    { name: "FAQ", href: "#faq" },
    { name: "Contact", href: "https://astranova2k26.com/contact" },
  ];

  const socialIcons = [
    { Icon: Instagram, href: "https://www.instagram.com/astranova_2k26/", label: "Instagram" },
    { Icon: Linkedin, href: "https://www.linkedin.com/in/astranova-tech-symposium-052028352/", label: "LinkedIn" },
  ];

  return (
    <footer
      className={className}
      style={{
        backgroundColor: "var(--color-background)",
        borderTop: "2px solid var(--color-border)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated top border */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "2px",
          background:
            "linear-gradient(90deg, transparent, var(--color-primary), var(--color-accent), transparent)",
          animation: "borderGlow 4s ease-in-out infinite",
        }}
      />

      {/* Grid overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(var(--color-border) 1px, transparent 1px),
            linear-gradient(90deg, var(--color-border) 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
          opacity: 0.03,
          pointerEvents: "none",
        }}
      />

      <div className="footer-container">
        <div className="footer-grid">
          {/* Logo Section */}
          <div className="footer-logo-section">
            <img
              src="https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/website/ASTRANOVA.png"
              alt="Astranova Logo"
              className="footer-logo"
            />
          </div>

          {/* Quick Links */}
          <div className="footer-links-section">
            <h3 className="footer-heading">Quick Links</h3>

            <ul className="footer-links-grid">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="footer-link">
                    ▸ {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Follow Us */}
          <div className="footer-social-section">
            <h3 className="footer-heading">Follow Us</h3>

            <div className="footer-social-icons">
              {socialIcons.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className="footer-social-icon"
                >
                  <Icon size={20} />
                </a>
              ))}
            </div>
            
            <p className="footer-social-text">
              Join our cosmic community and stay updated.
            </p>
          </div>
        </div>

        {/* Divider */}
        <div className="footer-divider" />

        {/* Bottom Bar */}
        <div className="footer-bottom">
          <p className="footer-copyright">© {currentYear} ASTRANOVA 2026. All rights reserved.</p>
        </div>
      </div>

      {/* Styles */}
      <style>{`
        @keyframes borderGlow {
          0%, 100% { opacity: 0.3; transform: translateX(-100%); }
          50% { opacity: 1; transform: translateX(100%); }
        }

        .footer-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 60px 20px 24px;
          position: relative;
        }

        .footer-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 40px;
          margin-bottom: 40px;
        }

        .footer-logo-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .footer-logo {
          max-width: 180px;
          width: 100%;
          height: auto;
          margin-bottom: 16px;
          transition: transform 0.3s ease, filter 0.3s ease;
        }

        .footer-logo:hover {
          transform: scale(1.05);
          filter: brightness(1.2);
        }

        .footer-description {
          color: var(--color-text-secondary);
          font-size: 0.85rem;
          line-height: 1.6;
          font-family: monospace;
          max-width: 400px;
          margin: 0;
        }

        .footer-links-section,
        .footer-social-section {
          display: flex;
          flex-direction: column;
        }

        .footer-heading {
          color: var(--color-text);
          font-weight: 700;
          font-size: 0.95rem;
          margin-bottom: 20px;
          text-transform: uppercase;
          letter-spacing: 2px;
          font-family: monospace;
          text-align: center;
          margin-top: 0;
        }

        .footer-links-grid {
          list-style: none;
          padding: 0;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          max-width: 400px;
          width: 100%;
        }

        .footer-link {
          color: var(--color-text-secondary);
          text-decoration: none;
          font-family: monospace;
          display: inline-block;
          transition: all 0.3s ease;
          position: relative;
        }

        .footer-link:hover {
          color: var(--color-primary);
          transform: translateX(4px);
        }

        .footer-social-icons {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
          justify-content: center;
        }

        .footer-social-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          border: 1px solid var(--color-border);
          color: var(--color-primary);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          text-decoration: none;
        }

        .footer-social-icon:hover {
          transform: scale(1.1) translateY(-2px);
          border-color: var(--color-primary);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .footer-social-text {
          color: var(--color-text-secondary);
          font-size: 0.8rem;
          line-height: 1.6;
          font-family: monospace;
          text-align: center;
          max-width: 400px;
          margin: 0 auto;
        }

        .footer-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--color-border), transparent);
          margin-bottom: 24px;
        }

        .footer-bottom {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 16px;
          font-family: monospace;
          font-size: 0.8rem;
          color: var(--color-text-secondary);
        }

        .footer-copyright {
          margin: 0;
        }

        .footer-legal-links {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .footer-legal-link {
          color: var(--color-text-secondary);
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .footer-legal-link:hover {
          color: var(--color-primary);
        }

        /* Tablet: 768px and up */
        @media (min-width: 768px) {
          .footer-container {
            padding: 70px 32px 28px;
          }

          .footer-grid {
            gap: 50px;
            margin-bottom: 50px;
          }

          .footer-logo {
            max-width: 200px;
            margin-bottom: 20px;
          }

          .footer-description,
          .footer-social-text {
            font-size: 0.9rem;
          }

          .footer-heading {
            font-size: 1rem;
            letter-spacing: 2.5px;
          }

          .footer-links-grid {
            max-width: 450px;
            gap: 14px;
          }

          .footer-bottom {
            font-size: 0.85rem;
          }

          .footer-legal-links {
            gap: 24px;
          }
        }

        /* Desktop: 1024px and up */
        @media (min-width: 1024px) {
          .footer-container {
            padding: 80px 40px 32px;
          }

          .footer-grid {
            grid-template-columns: 1.2fr 1fr 1fr;
            gap: 60px;
            margin-bottom: 60px;
          }

          .footer-logo-section {
            align-items: flex-start;
            text-align: left;
          }

          .footer-logo {
            max-width: 220px;
            margin-bottom: 24px;
          }

          .footer-description {
            max-width: none;
          }

          .footer-heading {
            text-align: left;
          }

          .footer-links-grid {
            margin: 0;
          }

          .footer-social-icons {
            justify-content: flex-start;
            margin-bottom: 20px;
          }

          .footer-social-text {
            text-align: left;
            margin: 0;
          }

          .footer-bottom {
            flex-direction: row;
            justify-content: space-between;
            text-align: left;
          }
        }

        /* Large Desktop: 1280px and up */
        @media (min-width: 1280px) {
          .footer-logo {
            max-width: 240px;
          }

          .footer-description {
            font-size: 0.95rem;
          }

          .footer-social-text {
            font-size: 0.85rem;
          }
        }
      `}</style>
    </footer>
  );
};

export default Footer;